import { Injectable } from '@angular/core';
import { AnalyticsInfoResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { GetSocialListeningMyBrandAnalyticsInfoService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-info.service';
import { displayEntityName } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/display-entity-name.util';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { ReportDateRange } from '../models/preview/preview.models';
import { ReportBlocksService } from './report-blocks.service';

interface CachedRead<T> {
	key: string;
	read$: Observable<T>;
}

/**
 * The reads several parts of one report need, made once per report page.
 *
 * Every slide fetches its own numbers, which is right for numbers only that
 * slide shows. Two do not fit that: the brand's KPI totals, which the
 * executive summary draws AND the thin-data gate decides eleven sections by,
 * and the brand's own name, which every source line prints. Asked for by each
 * slide separately they would be fetched a dozen times per page, and — worse
 * for the KPIs — could come back from two different moments and disagree.
 *
 * One entry per read, keyed by what the read depends on. A report page reads
 * one brand for one window, so the entry is replaced rather than accumulated:
 * reopening the report makes a new window and so a fresh read, which is what
 * "the numbers are read live" promises. A failed read is not kept, so the next
 * page to ask tries again instead of inheriting the failure.
 *
 * Both reads work without a session: the analytics tree and the block
 * allowlist accept the PDF export's token, which is why the brand's name comes
 * from the allowlist rather than from the brand list, which does not.
 */
@Injectable({ providedIn: 'root' })
export class ReportSharedReadsService {
	private kpiCache: CachedRead<AnalyticsInfoResponse | null> | null = null;
	private labelCache: CachedRead<string | null> | null = null;

	constructor(
		private analyticsInfo: GetSocialListeningMyBrandAnalyticsInfoService,
		private blocks: ReportBlocksService
	) {}

	/**
	 * The brand's KPI totals for the window, as the executive summary draws
	 * them. A failed read comes back as the same all-zero response the
	 * analytics service has always answered with, so the slide keeps
	 * degrading the way it did.
	 */
	kpis(
		brandId: string,
		range: ReportDateRange
	): Observable<AnalyticsInfoResponse> {
		return this.kpiRead(brandId, range).pipe(
			map(info => info ?? this.analyticsInfo.emptyResponse())
		);
	}

	/**
	 * Total mentions in the window, EXACT match — the executive summary's
	 * Mentions tile, from the same response. Null when it could not be read,
	 * which callers must treat as unknown rather than as none.
	 */
	mentionTotal(
		brandId: string,
		range: ReportDateRange
	): Observable<number | null> {
		return this.kpiRead(brandId, range).pipe(
			map(info => (info ? info.totalMentions : null))
		);
	}

	/**
	 * The report brand's name as a person reads it, or null when it cannot be
	 * read — in which case a source line simply does not name it.
	 */
	brandLabel(brandId: string): Observable<string | null> {
		if (this.labelCache?.key !== brandId) {
			const key = brandId;
			this.labelCache = {
				key,
				read$: this.blocks.comparableEntities(brandId).pipe(
					map(entities => {
						const self = entities.find(entity => entity.is_self);
						return self
							? displayEntityName(self.label) || null
							: null;
					}),
					catchError(() => of(null)),
					tap(label => this.forgetFailure(label, key, 'label')),
					shareReplay({ bufferSize: 1, refCount: false })
				)
			};
		}
		return this.labelCache.read$;
	}

	/**
	 * Keyed on the exact instants, not the period: two windows for "last 30
	 * days" opened a minute apart are two different reads, and serving one
	 * for the other is how a tile and a gate would end up disagreeing.
	 */
	private kpiRead(
		brandId: string,
		range: ReportDateRange
	): Observable<AnalyticsInfoResponse | null> {
		const key = `${brandId}|${range.start}|${range.end}`;
		if (this.kpiCache?.key !== key) {
			this.kpiCache = {
				key,
				read$: this.analyticsInfo
					.fetchOrNull(
						brandId,
						new Date(range.start),
						undefined,
						new Date(range.end)
					)
					.pipe(
						tap(info => this.forgetFailure(info, key, 'kpi')),
						shareReplay({ bufferSize: 1, refCount: false })
					)
			};
		}
		return this.kpiCache.read$;
	}

	/**
	 * Drop a read that failed, so the next page to ask makes it again. Only
	 * the entry this read created: by the time it fails another page may
	 * already have replaced it with a read of its own.
	 */
	private forgetFailure(
		value: unknown,
		key: string,
		cache: 'kpi' | 'label'
	): void {
		if (value !== null) {
			return;
		}
		if (cache === 'kpi' && this.kpiCache?.key === key) {
			this.kpiCache = null;
		}
		if (cache === 'label' && this.labelCache?.key === key) {
			this.labelCache = null;
		}
	}
}
